import { useEffect, useRef, useState } from "react";
import axios from "axios";

export default function AdaptLessonGoal({ lesson, onSaved, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const locked = useRef(false);
  const request = useRef(null);
  const dialog = useRef(null);
  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, [open]);
  const save = async event => {
    event.preventDefault();
    if (!goal.trim() || locked.current || disabled) return;
    const control = new AbortController(); request.current = control;
    locked.current = true; setBusy(true); setError("");
    try {
      const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-mode/lessons/${lesson.id}/goal`,
        { goal: goal.trim(), expected_revision: lesson.goal_revision || 0 }, { withCredentials: true, timeout: 20000, signal: control.signal });
      if (control.signal.aborted) return;
      if (data.lesson?.id !== lesson.id || data.lesson?.student_id !== lesson.student_id || data.lesson?.run_my_day_plan?.desired_focus !== goal.trim()) throw new Error("The saved lesson could not be confirmed.");
      onSaved(data.lesson); setOpen(false);
    } catch (failure) {
      if (control.signal.aborted) return;
      if (failure?.response?.status === 409 || ["ECONNABORTED", "ETIMEDOUT"].includes(failure?.code)) {
        try {
          const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-mode/lessons/active`, { withCredentials: true, timeout: 20000, signal: control.signal });
          if (control.signal.aborted) return;
          if (data.lesson?.id === lesson.id && data.lesson.student_id === lesson.student_id) onSaved(data.lesson, false);
        } catch { /* Keep the original failure and entered text for retry. */ }
      }
      if (control.signal.aborted) return;
      const detail = failure?.response?.data?.detail;
      setError((typeof detail === "string" ? detail : detail?.message) || failure.message || "Could not save the goal. Try again.");
    } finally { if (!control.signal.aborted) { locked.current = false; setBusy(false); } }
  };
  return <>
    <button type="button" disabled={disabled} data-testid="adapt-lesson-goal" className="min-h-11 rounded-lg border border-white/30 bg-slate-950/80 px-3 text-sm font-semibold" onClick={() => {
      setGoal(lesson.run_my_day_plan?.desired_focus || lesson.pre_lesson_focus?.join(", ") || ""); setError(""); setOpen(true);
    }}>Adapt</button>
    {open && <dialog ref={dialog} aria-labelledby="adapt-goal-title"
      onCancel={event => { event.preventDefault(); event.stopPropagation(); if (!busy) setOpen(false); }}
      className="fixed inset-0 m-0 h-dvh w-full max-h-none max-w-none border-0 bg-transparent p-0 text-white">
      <div className="flex min-h-full items-center justify-center bg-slate-950/85 p-4">
      <form onSubmit={save} className="max-h-[90dvh] overflow-y-auto w-full max-w-md space-y-4 rounded-2xl border border-slate-600 bg-slate-900 p-5 text-white">
        <h2 id="adapt-goal-title" className="text-xl font-bold">What are we working on now?</h2>
        <p className="text-sm text-slate-300">Make changes while parked. Your challenge and support choices stay as they are.</p>
        <label className="block">Lesson goal<input data-testid="adapt-goal-input" value={goal} onChange={e => setGoal(e.target.value)} maxLength={240}
          className="mt-2 min-h-12 w-full rounded-lg bg-slate-800 p-3" /></label>
        {error && <p role="alert" className="text-sm text-amber-200">{error}</p>}
        <div className="flex flex-wrap gap-3"><button type="submit" data-testid="adapt-goal-save" disabled={disabled || busy || !goal.trim()} className="min-h-12 rounded-lg bg-emerald-600 px-4 font-bold">{busy ? "Saving…" : "Use this goal"}</button>
          <button type="button" disabled={busy} onClick={() => setOpen(false)} className="min-h-12 rounded-lg border px-4">Cancel</button></div>
      </form>
      </div>
    </dialog>}
  </>;
}

